import RangeSlider from 'components/Basic/RangeSlider/RangeSlider';
import {
    filterGroupTitleTwClass,
    filterGroupTwClass,
    getFilterGroupArrowTwClass,
    getFilterGroupContentTwClass,
} from 'components/Blocks/Product/Filter/FilterGroup/FilterGroup';
import { getPriceRounded } from 'helpers/mappers/price';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useCallback, useState } from 'react';

type FilterGroupPriceProps = {
    title: string;
    initialMinPrice: string;
    initialMaxPrice: string;
    filteredMinPrice?: string | null;
    filteredMaxPrice?: string | null;
};

const TEST_IDENTIFIER = 'blocks-product-filter-filtergroup-price';

export const FilterGroupPrice: FC<FilterGroupPriceProps> = ({ title, initialMinPrice, initialMaxPrice }) => {
    const [isGroupOpen, setIsGroupOpen] = useState(true);
    const { updateFilterPriceMinimumQuery, updateFilterPriceMaximumQuery } = useUpdateFilterQuery();
    const filterQuery = useCurrentFilterQuery();

    const { minimalPrice, maximalPrice } = filterQuery || {};

    // Use absolute price range for stable slider bounds — dynamic filteredMin/MaxPrice
    // causes a feedback loop: GQL response shifts bounds → clamp resets thumb → filter gets cleared
    const minPriceOption = getPriceRounded(initialMinPrice);
    const maxPriceOption = getPriceRounded(initialMaxPrice);

    const onSetMinimalPrice = useCallback(
        (value: number) => {
            updateFilterPriceMinimumQuery(minPriceOption === value ? undefined : value);
        },
        [minPriceOption, updateFilterPriceMinimumQuery],
    );

    const onSetMaximalPrice = useCallback(
        (value: number) => {
            updateFilterPriceMaximumQuery(maxPriceOption === value ? undefined : value);
        },
        [updateFilterPriceMaximumQuery, maxPriceOption],
    );

    return (
        <div className={filterGroupTwClass} data-testid={TEST_IDENTIFIER}>
            <div className={filterGroupTitleTwClass} onClick={() => setIsGroupOpen((prev) => !prev)}>
                {title}
                <ArrowIcon className={getFilterGroupArrowTwClass(isGroupOpen)} />
            </div>
            <div className={getFilterGroupContentTwClass(isGroupOpen)}>
                <RangeSlider
                    max={maxPriceOption}
                    maxValue={maximalPrice ?? maxPriceOption}
                    min={minPriceOption}
                    minValue={minimalPrice ?? minPriceOption}
                    setMaxValueCallback={onSetMaximalPrice}
                    setMinValueCallback={onSetMinimalPrice}
                />
            </div>
        </div>
    );
};
